import React, { useState } from "react";

interface EmailVerificationPopupProps {
  onClose: () => void;
  onVerify: (verificationCode?: string) => void;
  onResendEmail?: () => void;
}

const EmailVerificationPopup: React.FC<EmailVerificationPopupProps> = ({ onClose, onVerify, onResendEmail }) => {
  const [isVerifying, setIsVerifying] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [verificationCode, setVerificationCode] = useState("");

  const handleVerifyClick = async () => {
    try {
      setIsVerifying(true);
      await onVerify(verificationCode);
    } catch (error) {
      console.error("이메일 인증 확인 실패:", error);
    } finally {
      setIsVerifying(false);
    }
  };

  const handleResendClick = async () => {
    if (onResendEmail) {
      try {
        setIsResending(true);
        await onResendEmail();
      } catch (error) {
        console.error("이메일 재발송 실패:", error);
      } finally {
        setIsResending(false);
      }
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl p-6 mx-4 max-w-md w-full shadow-2xl">
        {/* 팝업 헤더 */}
        <div className="text-center mb-6">
          <div className="w-16 h-16 mx-auto mb-4 bg-[#CF3688]/10 rounded-full flex items-center justify-center">
            <svg className="w-8 h-8 text-[#CF3688]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">
            이메일 인증이 필요합니다
          </h2>
        </div>

        {/* 팝업 내용 */}
        <div className="space-y-4 mb-6">
          <p className="text-gray-600 text-center leading-relaxed">
            서비스를 정상적으로 사용하기 위해 이메일 인증이 필요합니다.
          </p>
          
          <p className="text-gray-600 text-center leading-relaxed">
            인증 이메일 발송을 위해 아래 버튼을 클릭해주세요.
          </p>
          
          {/* 인증메일 발송 버튼 */}
          {onResendEmail && (
            <div className="flex justify-center">
              <button
                onClick={handleResendClick}
                disabled={isResending}
                className={`px-6 py-2 rounded-lg font-medium transition-all duration-200 ${
                  isResending
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {isResending ? (
                  <div className="flex items-center space-x-2">
                    <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span>발송 중...</span>
                  </div>
                ) : (
                  '인증메일 발송'
                )}
              </button>
            </div>
          )}
          
          <p className="text-gray-600 text-center leading-relaxed">
            메일을 받으셨다면 아래 입력란에 인증번호를 입력해주세요.
          </p>
        </div>

        {/* 인증번호 입력란 */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            인증번호
          </label>
          <input
            type="text"
            value={verificationCode}
            onChange={(e) => setVerificationCode(e.target.value)}
            placeholder="인증번호를 입력해주세요"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#CF3688] focus:border-transparent"
            disabled={isVerifying}
          />
        </div>

        {/* 인증 확인 버튼 */}
        <div className="mb-6">
          <button
            onClick={handleVerifyClick}
            disabled={isVerifying || !verificationCode.trim()}
            className={`w-full py-3 px-4 rounded-lg font-medium transition-all duration-200 ${
              isVerifying || !verificationCode.trim()
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-[#CF3688] text-white hover:bg-[#B82F77] shadow-sm hover:shadow-md'
            }`}
          >
            {isVerifying ? (
              <div className="flex items-center justify-center space-x-2">
                <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span>확인 중...</span>
              </div>
            ) : (
              '인증 확인'
            )}
          </button>
        </div>

        {/* 안내 텍스트 */}
        <div className="mt-4 text-center">
          <p className="text-xs text-gray-400">
            이메일을 받지 못하셨나요? 스팸함을 확인해보세요.
          </p>
        </div>
      </div>
    </div>
  );
};

export default EmailVerificationPopup;
